import { formatDate, newLogger } from "@/utils/util";
import network from "../../../utils/network";
import StoreCore from "../../StoreCore";
import dayjs from "dayjs";

let logger = newLogger("WorkLogStore");

const urls = {
  employeeTime: "/enterprise/user/timesheet",
  myTime: "/enterprise/mine/timesheet",
  employeeTimeExport: (user, urlSearchParams) =>
    `/enterprise/user/timesheet/export/${encodeURIComponent(
      user
    )}?${urlSearchParams.toString()}`,
  myTimeExport: `/enterprise/mine/timesheet/export`,
  refreshSpecific: (user, urlSearchParams) =>
    `/enterprise/user/timesheet/${encodeURIComponent(
      user
    )}/refresh?${urlSearchParams.toString()}`,
  expectedHours: "/census/work-schedule/expected-hours",
  worklog: "/enterprise/worklog",
  worklogs: "/enterprise/worklogs",
  worklogImport: "/enterprise/worklog/import",
  worklogsForProjects: "/enterprise/project-worklog",
};

/**
 * The Worklog store handles data about employee timesheets / worklogs
 */

const initState = () => {
  return {
    expectedWeek: {},
    myExpectedWeek: {},
    worklogWeek: {},
    worklogWeekForUser: {},
    myWorklogWeek: {},
    myLoggingRequired: [],
    expected: {
      2020: {},
      2021: {},
      2022: {},
      2023: {},
      2024: {},
      2025: {},
      2026: {},
      2027: {},
      2028: {},
    },
    myExpected: {
      2020: {},
      2021: {},
      2022: {},
      2023: {},
      2024: {},
      2025: {},
      2026: {},
      2027: {},
      2028: {},
    },
    worklogs: {
      2020: {},
      2021: {},
      2022: {},
      2023: {},
      2024: {},
      2025: {},
      2026: {},
      2027: {},
      2028: {},
    },
    myWorklogs: {
      2020: {},
      2021: {},
      2022: {},
      2023: {},
      2024: {},
      2025: {},
      2026: {},
      2027: {},
      2028: {},
    },
    logs: {},
    worklogsForProjects: [],
    singleLog: {},
  };
};

let generateRequestId = function () {
  return Math.floor(Math.random() * 100000);
};

const storeCore = new StoreCore();
const actions = {
  fetch: async function ({ commit, dispatch }, { params, force }) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("year", params.year);
      urlSearchParams.append("month", params.month);
      let data = await this.remoteRequest(
        "get",
        `${urls.employeeTime}/${encodeURIComponent(
          params.user
        )}?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Timesheetek betöltése...",
        null,
        force
      );
      commit("save", {
        user: params.user,
        year: params.year,
        month: params.month,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchMine: async function ({ commit, dispatch }, { params, force }) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("year", params.year);
      urlSearchParams.append("month", params.month);
      let data = await this.remoteRequest(
        "get",
        `${urls.myTime}?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Timesheejeim betöltése...",
        null,
        force
      );
      commit("saveMine", {
        year: params.year,
        month: params.month,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchWorklogs: async function ({ dispatch, commit }, { from, to, force }) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("year", params.year);
      urlSearchParams.append("month", params.month);
      let data = await this.remoteRequest(
        "get",
        `${urls.worklog}?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Logok betöltése...",
        null,
        force
      );
      commit("saveLogs", { from, data });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchWorklog: async function ({ dispatch, commit }, worklogId) {
    try {
      let data = await this.remoteRequest(
        "get",
        `${urls.worklog}/${worklogId}`,
        null,
        true,
        dispatch,
        "Logbejegyzés betöltése...",
        null,
        force
      );
      commit("saveLog", data);
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchWorklogsForProjects: async function ({ dispatch, commit }, projectIds) {
    try {
      const urlSearchParams = new URLSearchParams();
      for (let projectId of projectIds) {
        urlSearchParams.append("p", projectId);
      }
      let data = await this.remoteRequest(
        "get",
        urls.worklogsForProjects + "?" + urlSearchParams.toString(),
        null,
        true,
        dispatch,
        "Logok betöltése...",
        null,
        false
      );
      commit("saveForProjects", data);
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  exportMine: async function ({ dispatch }, { year, month }) {
    let requestId = generateRequestId();
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append("year", year);
    urlSearchParams.append("month", month);
    dispatch(
      "loading/startLoading",
      {
        id: requestId,
        type: "GET",
        url: urls.myTimeExport + "?" + urlSearchParams.toString(),
        flair: "Timesheet exportálása...",
      },
      { root: true }
    );

    try {
      const axios = await network.excelConnection();
      const { data } = await axios.get(
        urls.myTimeExport + "?" + urlSearchParams.toString()
      );

      dispatch(
        "loading/finishLoading",
        { id: requestId, result: true },
        { root: true }
      );

      return data;
    } catch (err) {
      logger.error(err);
      dispatch(
        "loading/finishLoading",
        { id: requestId, result: false },
        { root: true }
      );
    }
  },
  export: async function ({ dispatch }, { user, year, month }) {
    let requestId = generateRequestId();
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append("year", year);
    urlSearchParams.append("month", month);
    dispatch(
      "loading/startLoading",
      {
        id: requestId,
        type: "GET",
        url: urls.employeeTimeExport(user, urlSearchParams),
        flair: "Timesheet exportálása...",
      },
      { root: true }
    );

    try {
      const axios = await network.excelConnection();
      const { data } = await axios.get(
        urls.employeeTimeExport(user, urlSearchParams)
      );

      dispatch(
        "loading/finishLoading",
        { id: requestId, result: true },
        { root: true }
      );

      return data;
    } catch (err) {
      logger.error(err);
      dispatch(
        "loading/finishLoading",
        { id: requestId, result: false },
        { root: true }
      );
    }
  },
  fetchWeek: async function ({ commit, dispatch }, params) {
    try {
      let data = await this.remoteRequest(
        "get",
        `${urls.employeeTime}/${encodeURIComponent(
          params.from
        )}/${encodeURIComponent(params.to)}/${encodeURIComponent(params.user)}`,
        null,
        true,
        dispatch,
        "Timesheetek betöltése..."
      );
      commit("saveWeek", {
        weekStart: params.from,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchWeekForUser: async function ({ commit, dispatch }, params) {
    try {
      let data = await this.remoteRequest(
        "get",
        `${urls.employeeTime}/${encodeURIComponent(
          params.from
        )}/${encodeURIComponent(params.to)}/${encodeURIComponent(params.user)}`,
        null,
        true,
        dispatch,
        "Timesheetek betöltése..."
      );
      commit("saveWeekForUser", {
        user: params.user,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchMyWeek: async function ({ commit, dispatch }, params) {
    try {
      let data = await this.remoteRequest(
        "get",
        `${urls.myTime}/${encodeURIComponent(params.from)}/${encodeURIComponent(
          params.to
        )}`,
        null,
        true,
        dispatch,
        "Timesheetjeim betöltése..."
      );
      commit("saveMyWeek", {
        weekStart: params.from,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchExpected: async function ({ commit, dispatch }, params) {
    const fromDate = formatDate(new Date(params.year, params.month - 1, 1));
    const toDate = formatDate(new Date(params.year, params.month, 0));
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("from", fromDate);
      urlSearchParams.append("to", toDate);
      urlSearchParams.append("forceDate", "true");
      let data = await this.remoteRequest(
        "get",
        `${urls.expectedHours}/${encodeURIComponent(
          params.user
        )}?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Elvárt óraszámok betöltése..."
      );
      commit("saveExpected", {
        user: params.user,
        year: params.year,
        month: params.month,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchMyExpected: async function ({ commit, dispatch }, params) {
    const fromDate = formatDate(new Date(params.year, params.month - 1, 1));
    const toDate = formatDate(new Date(params.year, params.month, 0));
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("from", fromDate);
      urlSearchParams.append("to", toDate);
      let data = await this.remoteRequest(
        "get",
        `${urls.expectedHours}/mine?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Elvárt óraszámaim betöltése..."
      );
      commit("saveMyExpected", {
        year: params.year,
        month: params.month,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchExpectedWeek: async function ({ commit, dispatch }, params) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("from", params.from);
      urlSearchParams.append("month", params.to);
      let data = await this.remoteRequest(
        "get",
        `${urls.expectedHours}/${encodeURIComponent(
          params.user
        )}?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Elvárt óraszámok betöltése..."
      );
      commit("saveExpectedWeek", {
        weekStart: params.from,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchMyExpectedWeek: async function ({ commit, dispatch }, params) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("from", params.from);
      urlSearchParams.append("to", params.to);
      let data = await this.remoteRequest(
        "get",
        `${urls.expectedHours}/mine?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Elvárt óraszámaim betöltése..."
      );
      commit("saveMyExpectedWeek", {
        weekStart: params.from,
        data: data,
      });
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  fetchMyLoggingRequired: async function ({ commit, dispatch }, params) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("from", params.from);
      urlSearchParams.append("to", params.to);
      let data = await this.remoteRequest(
        "get",
        `${urls.expectedHours}/mine?${urlSearchParams.toString()}`,
        null,
        true,
        dispatch,
        "Elvárt óraszámaim betöltése...",
        null
      );
      commit("saveMyLoggingRequired", data);
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  refreshSpecific: async function ({ dispatch }, params) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("year", params.year);
      urlSearchParams.append("month", params.month);
      await this.remoteRequest(
        "get",
        urls.refreshSpecific(params.user, urlSearchParams),
        null,
        false,
        dispatch,
        "Timesheetek frissítése..."
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  refreshMineSpecific: async function ({ dispatch }, params) {
    try {
      const urlSearchParams = new URLSearchParams();
      urlSearchParams.append("year", params.year);
      urlSearchParams.append("month", params.month);
      await this.remoteRequest(
        "get",
        `${urls.myTime}/refresh?${urlSearchParams.toString()}`,
        null,
        false,
        dispatch,
        "worklog.refreshMineSpecific.flair|Timesheeteim frissítése",
        undefined,
        undefined,
        false,
        true,
        {
          progress:
            "worklog.refreshMineSpecific.progress|Timesheeteim frissítése folyamatban...",
          fail: "worklog.refreshMineSpecific.fail|Timesheeteim frissítése sikertelen!",
          success:
            "worklog.refreshMineSpecific.success|Timesheeteim frissítése sikeres!",
        }
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),
  postWorklog: async function ({ commit, dispatch }, params) {
    try {
      return await this.remoteRequest(
        "post",
        urls.worklog,
        params,
        false,
        dispatch,
        "worklog.postWorklog.flair|Logolás",
        undefined,
        undefined,
        false,
        false,
        {
          progress: "worklog.postWorklog.progress|Logolás folyamatban...",
          fail: "worklog.postWorklog.fail|Logolás sikertelen!",
          success: "worklog.postWorklog.success|Logolás sikeres!",
        }
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
      return Promise.reject(err);
    }
  }.bind(storeCore),
  postWorklogs: async function ({ commit, dispatch }, params) {
    try {
      await this.remoteRequest(
        "post",
        urls.worklogs,
        params,
        false,
        dispatch,
        null,
        undefined,
        undefined,
        false,
        false
      );
      return Promise.resolve();
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
      return Promise.reject(err);
    }
  }.bind(storeCore),
  editWorklog: async function ({ commit, dispatch }, params) {
    try {
      return await this.remoteRequest(
        "put",
        urls.worklog,
        params,
        false,
        dispatch,
        "worklog.postWorklog.flair|Logolás frissítése",
        undefined,
        undefined,
        false,
        false,
        {
          progress: "worklog.postWorklog.progress|Logolás folyamatban...",
          fail: "worklog.postWorklog.fail|Logolás sikertelen!",
          success: "worklog.postWorklog.success|Logolás sikeres!",
        }
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
      return Promise.reject(err);
    }
  }.bind(storeCore),
  deleteWorklog: async function ({ commit, dispatch }, worklogId) {
    try {
      await this.remoteRequest(
        "delete",
        `${urls.worklog}/${encodeURIComponent(worklogId)}`,
        null,
        false,
        dispatch,
        "worklog.deleteWorklog.flair|Logolás törlése",
        undefined,
        undefined,
        false,
        false,
        {
          progress:
            "worklog.deleteWorklog.progress|Logolás törlése folyamatban...",
          fail: "worklog.deleteWorklog.fail|Logolás törlése sikertelen!",
          success: "worklog.deleteWorklog.success|Logolás törlése sikeres!",
        }
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
        return Promise.reject(err);
      }
    }
  }.bind(storeCore),
  exportTemplate: async function ({ dispatch }) {
    let requestId = generateRequestId();
    dispatch(
      "loading/startLoading",
      {
        id: requestId,
        type: "GET",
        url: `${urls.worklog}/template`,
        flair: "Sablon letoltése...",
      },
      { root: true }
    );

    try {
      const axios = await network.excelConnection();
      const { data } = await axios.get(`${urls.worklog}/template`);

      dispatch(
        "loading/finishLoading",
        { id: requestId, result: true },
        { root: true }
      );

      return data;
    } catch (err) {
      logger.error(err);
      dispatch(
        "loading/finishLoading",
        { id: requestId, result: false },
        { root: true }
      );
    }
  },
  importLogs: async function ({ dispatch }, fileForm) {
    try {
      return await this.remoteRequest(
        "post",
        urls.worklogImport,
        fileForm,
        false,
        dispatch,
        "Logok importálása...",
        null
      );
    } catch (err) {
      if ("Cached!" !== err) {
        logger.error(err);
      }
    }
  }.bind(storeCore),

  clearCache: async function ({}) {
    this.clearCoreCache();
  }.bind(storeCore),
};

const mutations = {
  save: (state, { user, year, month, data }) => {
    if (new Date(data.from) <= new Date() && new Date(data.to) >= new Date()) {
      data.to = formatDate(new Date());
    }
    data.totalSeconds =
      (data.totalTimespent || 0) +
      (data.totalUntrackedTimespent || 0) +
      (data.totalConflictedTimespent || 0); // for the progressBar
    for (let d in data.days) {
      let day = data.days[d];
      day.dailyTotalOvertimeSeconds =
        day.dailyCashOvertime + day.dailyShiftOvertime;
      day.dailyTotalSeconds =
        day.dailyTimespent +
        day.dailyConflictedTimespent +
        day.dailyUntrackedTimespent -
        day.dailyTotalOvertimeSeconds;
    }
    data.days = data.days.sort((fe, se) => fe.day.localeCompare(se.day));
    if (state.worklogs[year][user]) {
      delete state.worklogs[year][user][month];
      state.worklogs[year][user][month] = data;
    } else {
      state.worklogs[year][user] = {};
      state.worklogs[year][user][month] = data;
    }
  },
  saveMine: (state, { year, month, data }) => {
    if (new Date(data.from) <= new Date() && new Date(data.to) >= new Date()) {
      data.to = formatDate(new Date());
    }
    data.totalSeconds =
      (data.totalTimespent || 0) +
      (data.totalUntrackedTimespent || 0) +
      (data.totalConflictedTimespent || 0); // for the progressBar
    for (let d in data.days) {
      let day = data.days[d];
      day.dailyTotalOvertimeSeconds =
        day.dailyCashOvertime + day.dailyShiftOvertime;
      day.dailyTotalSeconds =
        day.dailyTimespent +
        day.dailyConflictedTimespent +
        day.dailyUntrackedTimespent -
        day.dailyTotalOvertimeSeconds;
    }
    data.days = data.days.sort((fe, se) => fe.day.localeCompare(se.day));
    if (state.myWorklogs[year]) {
      delete state.myWorklogs[year][month];
      state.myWorklogs[year][month] = data;
    } else {
      state.myWorklogs[year] = {};
      state.myWorklogs[year][month] = data;
    }
  },
  saveWeek: (state, { weekStart, data }) => {
    if (new Date(data.from) <= new Date() && new Date(data.to) >= new Date()) {
      data.to = formatDate(new Date());
    }
    data.totalSeconds =
      (data.totalTimespent || 0) +
      (data.totalUntrackedTimespent || 0) +
      (data.totalConflictedTimespent || 0); // for the progressBar
    for (let d in data.days) {
      let day = data.days[d];
      day.dailyTotalOvertimeSeconds =
        day.dailyCashOvertime + day.dailyShiftOvertime;
      day.dailyTotalSeconds =
        day.dailyTimespent +
        day.dailyConflictedTimespent +
        day.dailyUntrackedTimespent -
        day.dailyTotalOvertimeSeconds;
    }
    data.days = data.days.sort((fe, se) => fe.day.localeCompare(se.day));
    if (state.worklogWeek) {
      delete state.worklogWeek[weekStart];
    }
    state.worklogWeek[weekStart] = data;
  },
  saveWeekForUser: (state, { user, data }) => {
    if (new Date(data.from) <= new Date() && new Date(data.to) >= new Date()) {
      data.to = formatDate(new Date());
    }
    data.totalSeconds =
      (data.totalTimespent || 0) +
      (data.totalUntrackedTimespent || 0) +
      (data.totalConflictedTimespent || 0); // for the progressBar
    for (let d in data.days) {
      let day = data.days[d];
      day.dailyTotalOvertimeSeconds =
        day.dailyCashOvertime + day.dailyShiftOvertime;
      day.dailyTotalSeconds =
        day.dailyTimespent +
        day.dailyConflictedTimespent +
        day.dailyUntrackedTimespent -
        day.dailyTotalOvertimeSeconds;
    }
    data.days = data.days.sort((fe, se) => fe.day.localeCompare(se.day));
    if (state.worklogWeekForUser) {
      delete state.worklogWeekForUser[user];
    }
    state.worklogWeekForUser[user] = data;
  },
  saveMyWeek: (state, { weekStart, data }) => {
    if (new Date(data.from) <= new Date() && new Date(data.to) >= new Date()) {
      data.to = formatDate(new Date());
    }
    data.totalSeconds =
      (data.totalTimespent || 0) +
      (data.totalUntrackedTimespent || 0) +
      (data.totalConflictedTimespent || 0); // for the progressBar
    for (let d in data.days) {
      let day = data.days[d];
      day.dailyTotalOvertimeSeconds =
        day.dailyCashOvertime + day.dailyShiftOvertime;
      day.dailyTotalSeconds =
        day.dailyTimespent +
        day.dailyConflictedTimespent +
        day.dailyUntrackedTimespent -
        day.dailyTotalOvertimeSeconds;
    }
    data.days = data.days.sort((fe, se) => fe.day.localeCompare(se.day));
    if (state.myWorklogWeek) {
      delete state.myWorklogWeek[weekStart];
    }
    state.myWorklogWeek[weekStart] = data;
  },
  saveExpected: (state, { user, year, month, data }) => {
    let today = formatDate(new Date());

    data.days = data.days.filter((day) =>
      dayjs(day.day).isBefore(dayjs(today).add(1, "day"))
    );

    if (!state.expected[year]) {
      state.expected[year] = {};
    }
    if (state.expected[year][user]) {
      delete state.expected[year][user][month];
      state.expected[year][user][month] = data;
    } else {
      state.expected[year][user] = {};
      state.expected[year][user][month] = data;
    }
  },
  saveMyExpected: (state, { year, month, data }) => {
    let today = formatDate(new Date());

    data.days = data.days.filter((day) =>
      dayjs(day.day).isBefore(dayjs(today).add(1, "day"))
    );

    if (state.myExpected[year]) {
      delete state.myExpected[year][month];
      state.myExpected[year][month] = data;
    } else {
      state.myExpected[year] = {};
      state.myExpected[year][month] = data;
    }
  },
  saveExpectedWeek: (state, { weekStart, data }) => {
    let today = formatDate(new Date());

    data.days = data.days.filter((day) =>
      dayjs(day.day).isBefore(dayjs(today).add(1, "day"))
    );

    if (state.expectedWeek) {
      delete state.expectedWeek[weekStart];
    }
    state.expectedWeek[weekStart] = data;
  },
  saveMyExpectedWeek: (state, { weekStart, data }) => {
    let today = formatDate(new Date());

    data.days = data.days.filter((day) =>
      dayjs(day.day).isBefore(dayjs(today).add(1, "day"))
    );

    if (state.myExpectedWeek) {
      delete state.myExpectedWeek[weekStart];
    }
    state.myExpectedWeek[weekStart] = data;
  },
  saveMyLoggingRequired: (state, data) => {
    state.myLoggingRequired = data.days;
  },
  saveLogs: (state, { from, data }) => {
    state.logs[from] = data;
  },
  saveLog: (state, data) => {
    state.singleLog = data;
  },
  saveForProjects: (state, data) => {
    state.worklogsForProjects = data;
  },
};

const getters = {
  worklogOf: (state) => (user, year, month) => {
    if (!state.worklogs[year][user]) return undefined;
    return state.worklogs[year][user][month];
  },
  myWorklog: (state) => (year, month) => {
    if (!state.myWorklogs[year]) return undefined;
    return state.myWorklogs[year][month];
  },
  expectedOf: (state) => (user, year, month) => {
    if (!state.expected[year][user]) return undefined;
    return state.expected[year][user][month];
  },
  myExpected: (state) => (year, month) => {
    if (!state.myExpected[year]) return undefined;
    return state.myExpected[year][month];
  },
  expectedOfWeek: (state) => (weekStart) => {
    return state.expectedWeek[weekStart];
  },
  myExpectedOfWeek: (state) => (weekStart) => {
    return state.myExpectedWeek[weekStart];
  },
  worklogOfWeek: (state) => (weekStart) => {
    return state.worklogs[weekStart];
  },
  worklogOfWeekForUser: (state) => (user) => {
    return state.worklogWeekForUser[user];
  },
  myWorklogOfWeek: (state) => (weekStart) => {
    return state.myWorklogWeek[weekStart];
  },
  myLoggingRequired: (state) => {
    return state.myLoggingRequired;
  },
  worklogs: (state) => (weekStart) => {
    return state.logs[weekStart];
  },
  singleLog: (state) => {
    return state.singleLog;
  },
  worklogsForProjects: (state) => {
    return state.worklogsForProjects;
  },
};

export default {
  namespaced: true,
  state: initState(),
  mutations: mutations,
  actions: actions,
  getters: getters,
};
